// SlotManagement.js
import React, { useState, useEffect } from "react";
import {
  FaClock,
  FaToggleOn,
  FaToggleOff,
  FaCalendarAlt,
  FaBuilding,
  FaInfoCircle,
  FaSpinner
} from "react-icons/fa";
import axios from "axios";
import { useArenaAuth } from "../../context/ArenaAuthContext";

const SlotManagement = () => {
  const [slots, setSlots] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [facilitySlotAvailability, setFacilitySlotAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const { arenaUser } = useArenaAuth();

  const daysOfWeek = [
    { id: 1, name: "Monday", short: "Mon" },
    { id: 2, name: "Tuesday", short: "Tue" },
    { id: 3, name: "Wednesday", short: "Wed" },
    { id: 4, name: "Thursday", short: "Thu" },
    { id: 5, name: "Friday", short: "Fri" },
    { id: 6, name: "Saturday", short: "Sat" },
    { id: 7, name: "Sunday", short: "Sun" },
  ];

  useEffect(() => {
    fetchAllSlots();
    if (arenaUser?.arenaId) fetchArenaFacilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arenaUser?.arenaId]);

  useEffect(() => {
    if (selectedFacility) {
      fetchFacilitySlotAvailability();
    }
  }, [selectedFacility]);

  const fetchAllSlots = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/all-slots`
      );
      setSlots(response.data);
    } catch (error) {
      console.error("Error fetching slots:", error);
    }
  };

  const fetchArenaFacilities = async () => {
    try {
      const arenaId = arenaUser?.arenaId;
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/arena-facilities/${arenaId}`,
        {
          withCredentials: true,
        }
      );
      setFacilities(response.data);
      if (response.data.length > 0) {
        setSelectedFacility(response.data[0]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching arena facilities:", error);
      setLoading(false);
    }
  };

  const fetchFacilitySlotAvailability = async () => {
    try {
      const arenaId = arenaUser?.arenaId;
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/facility-slot-availability/${arenaId}/${selectedFacility.id}`,
        {
          withCredentials: true,
        }
      );
      setFacilitySlotAvailability(response.data);
    } catch (error) {
      console.error("Error fetching facility slot availability:", error);
      setFacilitySlotAvailability([]);
    }
  };

  const isSlotEnabledForDay = (slotId, dayOfWeek) => {
    const availability = facilitySlotAvailability.find(
      (item) => item.slot_id === slotId && item.day_of_week === dayOfWeek
    );
    return availability ? availability.is_enabled === 1 : false;
  };

  const toggleSlotForDay = async (slotId, dayOfWeek) => {
    try {
      const arenaId = arenaUser?.arenaId;
      const currentlyEnabled = isSlotEnabledForDay(slotId, dayOfWeek);

      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/facility-slot-toggle`,
        {
          arenaId: arenaId,
          facilityId: selectedFacility.id,
          slotId: slotId,
          dayOfWeek: dayOfWeek,
          isEnabled: !currentlyEnabled,
        },
        { withCredentials: true }
      );

      // Refresh the availability data
      fetchFacilitySlotAvailability();
    } catch (error) {
      console.error("Error toggling slot:", error);
      alert("Failed to update slot status");
    }
  };

  const toggleSlotForAllDays = async (slotId) => {
    try {
      const arenaId = arenaUser?.arenaId;

      // Check if the slot is enabled for any day
      const isEnabledForAnyDay = daysOfWeek.some((day) =>
        isSlotEnabledForDay(slotId, day.id)
      );

      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/facility-slot-bulk-toggle`,
        {
          arenaId: arenaId,
          facilityId: selectedFacility.id,
          slotId: slotId,
          isEnabled: !isEnabledForAnyDay,
        },
        { withCredentials: true }
      );

      // Refresh the availability data
      fetchFacilitySlotAvailability();
    } catch (error) {
      console.error("Error bulk toggling slot:", error);
      alert("Failed to update slot status for all days");
    }
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
          <FaClock className="text-[#0071bc] text-lg sm:text-xl md:text-2xl" />
          <span className="truncate">Slot Management</span>
        </h2>
        <p className="text-gray-600 text-xs sm:text-sm md:text-base">Manage time slot availability for your facilities</p>
      </div>

      {loading ? (
        <div className="text-center py-8 sm:py-12 md:py-16">
          <div className="inline-flex items-center gap-2 sm:gap-3 bg-white p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-lg">
            <FaSpinner className="animate-spin text-[#0071bc] text-lg sm:text-xl md:text-2xl" />
            <p className="text-gray-600 font-medium text-sm sm:text-base">Loading slot data...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Facility Selection */}
          <div className="bg-white p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 mb-4 sm:mb-6 md:mb-8">
            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3 sm:mb-4">
              <FaBuilding className="text-[#0071bc] text-sm sm:text-base" />
              Select Facility
            </h3>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {facilities.map((facility) => (
                <button
                  key={facility.id}
                  onClick={() => setSelectedFacility(facility)}
                  className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-medium text-xs sm:text-sm md:text-base transition-all duration-300 ${
                    selectedFacility?.id === facility.id 
                      ? "bg-[#0071bc] text-white shadow-lg" 
                      : "bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-[#0071bc]"
                  }`}
                >
                  {facility.sport_name}
                </button>
              ))}
            </div>
          </div>

          {selectedFacility && (
            <>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 sm:p-4 md:p-6 rounded-xl sm:rounded-2xl border border-blue-200 mb-4 sm:mb-6 md:mb-8">
                <h4 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900">
                  Managing slots for: <span className="text-[#0071bc] font-bold">{selectedFacility.sport_name}</span>
                </h4>
              </div>

              {/* Slot Management Grid - Mobile Responsive */}
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 overflow-x-auto">
                <div className="min-w-[640px]">
                  <div className="grid grid-cols-9 bg-gray-50 border-b border-gray-200">
                    <div className="p-2 sm:p-3 md:p-4 font-semibold text-gray-900 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm md:text-base">
                      <FaClock className="text-[#0071bc] text-xs sm:text-sm" />
                      <span className="hidden sm:inline">Time Slot</span>
                      <span className="sm:hidden">Time</span>
                    </div>
                    <div className="p-2 sm:p-3 md:p-4 font-semibold text-gray-900 text-center text-xs sm:text-sm md:text-base">All</div>
                    {daysOfWeek.map((day) => (
                      <div key={day.id} className="p-2 sm:p-3 md:p-4 font-semibold text-gray-900 text-center text-xs sm:text-sm md:text-base">
                        <div className="flex items-center justify-center gap-0.5 sm:gap-1">
                          <FaCalendarAlt className="text-green-500 text-[10px] sm:text-xs hidden sm:block" />
                          {day.short}
                        </div>
                      </div>
                    ))}
                  </div>

                  {slots.map((slot, index) => (
                    <div key={slot.slot_id} className={`grid grid-cols-9 border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                      <div className="p-2 sm:p-3 md:p-4 flex items-center gap-1 sm:gap-2 font-medium text-gray-900 text-xs sm:text-sm md:text-base">
                        <FaClock className="text-blue-500 text-[10px] sm:text-xs hidden sm:block" />
                        {slot.time}
                      </div>

                      {/* All Days Toggle */}
                      <div className="p-2 sm:p-3 md:p-4 flex justify-center">
                        <button
                          onClick={() => toggleSlotForAllDays(slot.slot_id)}
                          className="bg-[#0071bc] text-white px-1.5 sm:px-2 md:px-3 py-1 sm:py-1.5 md:py-2 rounded-md sm:rounded-lg text-[10px] sm:text-xs md:text-sm font-medium hover:bg-[#005a94] transition-all duration-300 flex items-center gap-1 shadow-md hover:shadow-lg"
                          title="Toggle for all days"
                        >
                          <FaToggleOn className="text-[10px] sm:text-xs" />
                          <span className="hidden md:inline">All</span>
                        </button>
                      </div>

                      {/* Individual Day Toggles */}
                      {daysOfWeek.map((day) => {
                        const isEnabled = isSlotEnabledForDay(
                          slot.slot_id,
                          day.id
                        );
                        return (
                          <div key={day.id} className="p-2 sm:p-3 md:p-4 flex justify-center">
                            <button
                              onClick={() =>
                                toggleSlotForDay(slot.slot_id, day.id)
                              }
                              className={`w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                                isEnabled 
                                  ? "bg-green-500 text-white shadow-lg hover:bg-green-600" 
                                  : "bg-gray-300 text-gray-600 hover:bg-gray-400"
                              }`}
                              title={`${isEnabled ? "Disable" : "Enable"} for ${
                                day.name
                              }`}
                            >
                              {isEnabled ? <FaToggleOn className="text-xs sm:text-sm md:text-lg" /> : <FaToggleOff className="text-xs sm:text-sm md:text-lg" />}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              {slots.length === 0 && (
                <div className="text-center py-8 sm:py-12 md:py-16 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl sm:rounded-2xl border-2 border-dashed border-gray-300">
                  <FaClock className="text-4xl sm:text-5xl md:text-6xl text-gray-400 mx-auto mb-2 sm:mb-4" />
                  <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-700 mb-1 sm:mb-2">No Time Slots Available</h3>
                  <p className="text-gray-600 text-xs sm:text-sm md:text-base">No time slots are configured for this facility.</p>
                </div>
              )}
            </>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 mt-4 sm:mt-6 md:mt-8">
            <h4 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 flex items-center gap-2 mb-2 sm:mb-3 md:mb-4">
              <FaInfoCircle className="text-[#0071bc] text-sm sm:text-base" />
              How to use Slot Management:
            </h4>
            <ul className="space-y-1.5 sm:space-y-2 text-gray-700 text-xs sm:text-sm md:text-base">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#0071bc] rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></span>
                Select a facility to manage its slot availability
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#0071bc] rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></span>
                Use individual day toggles to enable/disable slots for specific days
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#0071bc] rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></span>
                Use "All Days" button to quickly enable/disable a slot for the entire week
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></span>
                Green toggles indicate enabled slots, gray toggles indicate disabled slots
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[#0071bc] rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></span>
                Only enabled slots will be available for user bookings
              </li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default SlotManagement;
